import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetPrepStationsQuery,
  useDeletePrepStationMutation,
} from "../query/prepStationsQuery";
import type { PrepStationId } from "@KassaroAB/shared/types/ids";

export const PrepStationsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("prepStations");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<PrepStationId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: prepStations,
    isLoading,
    error: loadError,
  } = useGetPrepStationsQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeletePrepStationMutation(scope);

  const stationList = prepStations ?? [];
  const deleteName =
    stationList.find((station) => station.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("prepStationsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && stationList.length === 0) {
    return (
      <PageContent title={t("prepStationsView.title")}>
        <LoadingContainer message={t("prepStationsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("prepStationsView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("prepStationsView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("prepStationsView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("prepStationsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {stationList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("prepStationsView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("prepStationsView.tableScope")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("prepStationsView.tableMaster")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {stationList.map((station) => (
                <TableRow key={station.id} to={`${pagePath}/${station.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={station.name}
                      origin={station.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>
                    {t(`prepStationForm.servingScope${station.servingScope}`)}
                  </TableCell>
                  <TableCell noWrap>
                    {station.isMaster ? t("common.yes") : t("common.no")}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(station.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("prepStationsView.empty")} ${t("prepStationsView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("prepStationForm.deletePrepStation")}
        message={t("prepStationsView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
